import React from 'react';
import { Card, Row, Col, Table } from 'antd';
import { LineChartOutlined, BarChartOutlined } from '@ant-design/icons';
import ChartContainer from '../../components/shared/ChartContainer';
import { mockPerformanceData, mockBenchmarkData } from '../data/mockData';

const TrendAnalysis: React.FC = () => {
  const { trends } = mockPerformanceData;

  // Client growth trend chart
  const clientGrowthData = {
    labels: trends.clientGrowth.map(item => item.period),
    datasets: [
      {
        label: 'Client Count',
        data: trends.clientGrowth.map(item => item.value),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        fill: true,
      },
    ],
  };

  // Revenue growth trend chart
  const revenueGrowthData = {
    labels: trends.revenueGrowth.map(item => item.period),
    datasets: [
      {
        label: 'Revenue ($)',
        data: trends.revenueGrowth.map(item => item.value),
        borderColor: '#52c41a',
        backgroundColor: 'rgba(82, 196, 26, 0.1)',
        fill: true,
      },
    ],
  };

  // Coach performance trend chart
  const coachPerformanceData = {
    labels: trends.coachPerformance.map(item => item.period),
    datasets: [
      {
        label: 'Satisfaction Score',
        data: trends.coachPerformance.map(item => item.value),
        borderColor: '#722ed1',
        backgroundColor: 'rgba(114, 46, 209, 0.1)',
        fill: true,
      },
    ],
  };

  // Session metrics trend chart
  const sessionMetricsData = {
    labels: trends.sessionMetrics.map(item => item.period),
    datasets: [
      {
        label: 'Sessions Completed',
        data: trends.sessionMetrics.map(item => item.value),
        borderColor: '#faad14',
        backgroundColor: 'rgba(250, 173, 20, 0.1)',
        fill: true,
      },
    ],
  };

  // Benchmark comparison chart
  const benchmarkData = {
    labels: mockBenchmarkData.map(item => item.category),
    datasets: [
      {
        label: 'Our Performance',
        data: mockBenchmarkData.map(item => item.ourValue),
        backgroundColor: '#1890ff',
      },
      {
        label: 'Industry Average',
        data: mockBenchmarkData.map(item => item.industryAvg),
        backgroundColor: '#f0f0f0',
      },
    ],
  };

  // Benchmark table columns
  const benchmarkColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Our Value',
      dataIndex: 'ourValue',
      key: 'ourValue',
      render: (value: number) => value.toFixed(1),
    },
    {
      title: 'Industry Avg',
      dataIndex: 'industryAvg',
      key: 'industryAvg',
      render: (value: number) => value.toFixed(1),
    },
    {
      title: 'Percentile',
      dataIndex: 'percentile',
      key: 'percentile',
      render: (percentile: number) => `${percentile}th`,
    },
    {
      title: 'Performance',
      key: 'performance',
      render: (record: any) => {
        const ratio = record.ourValue / record.industryAvg;
        const color = ratio >= 1.1 ? '#52c41a' : ratio >= 1.0 ? '#1890ff' : ratio >= 0.9 ? '#faad14' : '#f5222d';
        const text = ratio >= 1.1 ? 'Excellent' : ratio >= 1.0 ? 'Above Average' : ratio >= 0.9 ? 'Average' : 'Below Average';
        return <span style={{ color }}>{text}</span>;
      },
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <ChartContainer
            title={
              <span>
                <LineChartOutlined /> Client Growth Trend
              </span>
            }
            type="area"
            data={clientGrowthData}
            height={300}
          />
        </Col>
        <Col span={12}>
          <ChartContainer
            title="Revenue Growth Trend"
            type="area"
            data={revenueGrowthData}
            height={300}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <ChartContainer
            title="Coach Performance Trend"
            type="line"
            data={coachPerformanceData}
            height={300}
          />
        </Col>
        <Col span={12}>
          <ChartContainer
            title="Session Completion Trend"
            type="line"
            data={sessionMetricsData}
            height={300}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={16}>
          <ChartContainer
            title={
              <span>
                <BarChartOutlined /> Industry Benchmark Comparison
              </span>
            }
            type="bar"
            data={benchmarkData}
            height={300}
          />
        </Col>
        <Col span={8}>
          <Card title="Performance Summary" style={{ height: '330px' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a', marginBottom: 4 }}>
                Strong
              </div>
              <div style={{ color: '#666' }}>Overall Performance</div>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <strong>Above Industry Average:</strong>
              <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                <li>Revenue Growth</li>
                <li>Client Retention</li>
                <li>Response Time</li>
              </ul>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <strong>Areas for Improvement:</strong>
              <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                <li>Coach Utilization</li>
                <li>Client Satisfaction</li>
              </ul>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Detailed Benchmark Analysis">
            <Table
              columns={benchmarkColumns}
              dataSource={mockBenchmarkData}
              rowKey="category"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TrendAnalysis; 